import type { DocumentVersion, Status } from '@ttab/elephant-api/repository'

export interface StatusHistoryEntry {
  key: string
  status: string
  version: bigint
  // Publication the step belongs to, i.e. the usable counter. Missing on the created entry
  publishVersion?: bigint
  created: string
  creator: string
  cause?: string
}

const SCHEDULER_URI = 'internal://scheduler'

/**
 * Merge statuses of all names into one timeline, newest first, with a leading
 * "created" entry from the first document version. Drafts are only kept once
 * the document has left draft, i.e. when it was sent back to draft.
 */
export function buildStatusHistory(
  statusesByName: Record<string, Status[]>,
  firstVersion?: DocumentVersion | null
): StatusHistoryEntry[] {
  const usables = statusesByName.usable ?? []
  const entries: StatusHistoryEntry[] = Object.entries(statusesByName)
    .flatMap(([name, statuses]) => statuses.map((status) => toEntry(name, status, usables)))

  const firstNonDraft = entries
    .filter((entry) => entry.status !== 'draft')
    .reduce<number | undefined>((earliest, entry) => {
      const time = toTime(entry.created)
      return earliest === undefined || time < earliest ? time : earliest
    }, undefined)

  const filtered = entries.filter((entry) => {
    if (entry.status !== 'draft') {
      return true
    }

    return firstNonDraft !== undefined && toTime(entry.created) > firstNonDraft
  })

  if (firstVersion) {
    filtered.push({
      key: 'created',
      status: 'created',
      version: firstVersion.version,
      created: firstVersion.created,
      creator: firstVersion.creator
    })
  }

  return filtered.sort((a, b) => toTime(b.created) - toTime(a.created))
}


function toEntry(name: string, status: Status, usables: Status[]): StatusHistoryEntry {
  const isUnpublish = name === 'usable' && status.version === -1n
  const creator = status.creator === SCHEDULER_URI
    ? status.meta?.['scheduled-by'] || status.creator
    : status.creator

  return {
    key: `${name}-${status.id}`,
    status: isUnpublish ? 'unpublished' : name,
    version: status.version,
    publishVersion: name === 'usable' ? status.id : nextPublishVersion(status, usables),
    created: status.created,
    creator,
    cause: status.meta?.cause || undefined
  }
}


/**
 * Steps between publications belong to the upcoming one: one past the latest usable
 * (publish or unpublish) set before the step.
 */
function nextPublishVersion(status: Status, usables: Status[]): bigint {
  const time = toTime(status.created)
  const latest = usables
    .filter((usable) => toTime(usable.created) < time)
    .reduce((max, usable) => (usable.id > max ? usable.id : max), 0n)

  return latest + 1n
}


function toTime(created: string): number {
  const time = new Date(created).getTime()
  return Number.isNaN(time) ? 0 : time
}
